import { AdvancedImage, lazyload } from "@cloudinary/react";
import moment from "moment";
import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { loggedInUser } from "src/apollo/cache";
import { AppText } from "src/Components/UI";
import { FlexDiv } from "src/Components/UI/FlexDiv";
import { PhoenixAppButton, PhoenixIcon } from "src/Components/UI/Phoenix";
import { ModalContext } from "src/context";
import {
  alert_circle,
  arrowsLeftAndRight,
  automated_mail,
  chevron_down,
  chevron_up,
  dollar_sign,
  edit,
  mail,
  mail_incoming,
  mail_outgoing,
  message_incoming,
  message_outgoing,
  message_square,
  non_call_icon,
  phone_incoming,
  phone_outgoing,
  play_circle,
  sequences,
  user_add,
  users,
} from "src/images/NewDesign";
import { cloudinary } from "src/services/cloudinary";
import { formatPhoneNumber } from "src/utils/format";
import { useClickOutside } from "src/utils/hooks";
import { sanitizeURL } from "src/utils/misc";
import { theme } from "src/utils/theme";
import { DEFAULT_CLOUDINARY_IMAGE } from "src/utils/variables";
import styled from "styled-components";
import { ContactTag } from "../../DashboardV2/DetailedLeadCard/ContactTab";

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const Timestamp: React.FC<{ data: any }> = ({ data }) => {
  const date = moment(data?.created_at).format("M/DD/YYYY");
  const time = moment(data?.created_at).format("h:mm A");
  return (
    <FlexDiv align="center" gap={4}>
      <AppText fontSize={12} fontWeight={400} lineHeight={18}>
        {date}
      </AppText>
      <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.secondary}>
        {time}
      </AppText>
    </FlexDiv>
  );
};

const formatExitType = (exitType: string) => {
  return exitType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const HistoryItem: React.FC<{ data: any; zIndex: number }> = ({ data, zIndex }) => {
  const Info = useMemo(() => {
    switch (data?.__typename) {
      case "Lead": {
        return (
          <FlexDiv justify="space-between" gap={4} align="center" width="100%" padding="8px">
            <FlexDiv align="center">
              <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
                Lead Created
              </HistoryItemTitle>
              <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
                {data?.lead_creation_source}
              </AppText>
            </FlexDiv>

            <Timestamp data={data} />
          </FlexDiv>
        );
      }
      case "LeadActivity": {
        if (data?.communication_type === "Email") {
          return <EmailInfo data={data} />;
        }
        if (data?.communication_type === "SMS") {
          return <SMSInfo data={data} />;
        }
        if (data?.communication_type === "Phone") {
          return <PhoneInfo data={data} />;
        }
        if (data?.communication_type === "ExternalTransfer") {
          return <ExternalTransferInfo data={data} />;
        }
        if (data?.communication_type === "NonCall") {
          return <NonCallInfo data={data} />;
        }
        return <></>;
      }
      case "LeadAssignmentHistory": {
        return <LeadAssignmentInfo data={data} />;
      }
      case "Sale": {
        return <SaleInfo data={data} />;
      }
      case "SequenceEntryExitLead": {
        return <SequenceEntryExitInfo data={data} />;
      }
      default:
        return <AppText>{data?.__typename}</AppText>;
    }
  }, [data]);

  const UnderInfo = useMemo(() => {
    switch (data?.__typename) {
      case "SequenceEntryExitLead":
        if (!data?.exit_type) return null;
        return (
          <UnderDataContainer>
            <FlexDiv gap={8} align="center">
              <AppText fontSize={12} fontWeight={500}>
                Exit Reason:
              </AppText>
              <AppText fontSize={12} color={theme.text.neutral.tertiary}>
                {formatExitType(data?.exit_type)}
              </AppText>
            </FlexDiv>
          </UnderDataContainer>
        );
      default:
        return null;
    }
  }, [data]);

  return (
    <HistoryItemContainer
      style={{
        position: "relative",
        zIndex,
      }}
    >
      <HistoryItemIndicator data={data} />
      <FlexDiv direction="column" width="100%">
        <InfoContainer hasUnderInfo={!!UnderInfo}>{Info}</InfoContainer>
        {UnderInfo}
      </FlexDiv>
    </HistoryItemContainer>
  );
};

const HistoryItemIndicator: React.FC<{ data: any }> = ({ data }) => {
  const icon = useMemo(() => {
    switch (data?.__typename) {
      case "Lead":
        return user_add;
      case "LeadActivity":
        if (data?.communication_type === "Phone") return data?.channel === "Outbound" ? phone_outgoing : phone_incoming;
        if (data?.communication_type === "Email")
          return data?.type === "Sequence Automated Email"
            ? automated_mail
            : data?.channel === "Outbound"
            ? mail
            : mail_incoming;
        if (data?.communication_type === "SMS") return data?.channel === "Outbound" ? message_square : message_incoming;
        if (data?.communication_type === "ExternalTransfer") return arrowsLeftAndRight;
        if (data?.communication_type === "NonCall") return non_call_icon;
        return alert_circle;
      case "LeadAssignmentHistory":
        return users;
      case "Sale":
        return dollar_sign;
      case "SequenceEntryExitLead":
        return sequences;
      default:
        return alert_circle;
    }
  }, [data]);
  const color = useMemo(() => {
    if (data?.title?.includes("Failed")) return theme.fill.danger.primary;
    switch (data?.__typename) {
      case "LeadActivity":
        return data?.channel === "Outbound" || data?.communication_type === "NonCall"
          ? theme.icon.brand.default
          : theme.fill.dataviz2.primary;
      case "LeadAssignmentHistory":
        return theme.fill.brand.inverse;
      case "Sale":
        return theme.fill.success.primary;
      case "SequenceEntryExitLead":
        return theme.surface.brand.primary;
      default:
        return theme.fill.brand.inverse;
    }
  }, [data]);
  const borderColor = useMemo(() => {
    switch (data?.__typename) {
      case "SequenceEntryExitLead":
        return theme.border.warning.primary;
      default:
        return null;
    }
  }, [data]);
  const iconColor = useMemo(() => {
    switch (data?.__typename) {
      case "SequenceEntryExitLead":
        return theme.icon.warning.default;
      default:
        return theme.icon.neutral.inverse;
    }
  }, [data]);

  return (
    <>
      <Ruler />
      <IndicatorContainer color={color} borderColor={borderColor}>
        <PhoenixIcon svg={icon} size={icon === non_call_icon ? 24 : 16} color={iconColor} hoverColor={iconColor} />
      </IndicatorContainer>
    </>
  );
};

const LeadAssignmentInfo: React.FC<{ data: any }> = ({ data }) => {
  const title = useMemo(() => {
    switch (data?.lead_assignment_origin) {
      case "SingleClaim":
        return "Claim";
      case "BulkClaimAllLeads":
        return "Bulk Claimed via All Leads";
      case "ReassignedAllLeads":
        return "Reassigned via All Leads";
      case "ReassignedLeadDetail":
        return "Reassigned";
      case "Released":
        return "Released";
      case "OpsiqUnassigned":
        return "Sellfire Unassigned";
      case "BulkUnassign":
        return "Bulk Unassigned";
      case "RulesOfEngagementAdjustment":
        return "Rules Of Engagement Adjustment";
      case "LiveTransfer":
        return "Live Transfer";
      case "SDRNonLiveTransfer":
        return "SDR Non Live Transfer";
      case "OpsiqClearFromQueue":
        return "Clear From Queue";
      case "OwnedOnInboundCall":
        return "Owned via Inbound Call";
      case "UnassignOnUserDelete":
        return "Unassign via User Delete";
      case "MovedToResting":
        return "Moved To Resting";
      case "MovedToRetired":
        return "Moved To Retired";
      case "AssociatedContactSync":
        return "Associated Contact Sync";
      case "SequenceEndAction":
        return "Sequence End Action";
      case "SalesforceAction":
        return "Salesforce Action";
      case "OwnedOnCSVImport":
        return "Owned via CSV Import";
      case "OwnedOnInboundCallRR":
        return "Owned via Inbound Call RR";
      default:
        return "Lead Assignment";
    }
  }, [data]);

  const initiatedBy = useMemo(() => data?.sender_user?.full_name || data?.custom_who_initiated, [data]);

  const [expanded, setExpanded] = useState(false);

  return (
    <FlexDiv direction="column" width="100%">
      <FlexDiv
        justify="space-between"
        gap={4}
        align="center"
        width="100%"
        height={38}
        padding="8px"
        style={{ borderBottom: expanded ? `1px solid ${theme.border.neutral.secondary}` : "unset" }}
      >
        <FlexDiv align="center" gap={12}>
          <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
            Ownership Change
          </HistoryItemTitle>

          <FlexDiv align="center" gap={4}>
            {!!data?.old_user ? (
              <FlexDiv align="center" gap={4}>
                <AdvancedImageStyle
                  cldImg={cloudinary.image(data.old_user?.profile_image || DEFAULT_CLOUDINARY_IMAGE)}
                  plugins={[lazyload()]}
                />
                <AppText fontSize={12} fontWeight={600} lineHeight={18}>
                  {data?.old_user?.first_name} {data?.old_user?.last_name}
                </AppText>
              </FlexDiv>
            ) : (
              <AppText fontSize={12} fontWeight={600} lineHeight={18}>
                Unassigned
              </AppText>
            )}

            <AppText fontSize={12} color={theme.text.neutral.tertiary}>
              to
            </AppText>

            {!!data?.receiver_user ? (
              <FlexDiv align="center" gap={4}>
                <AdvancedImageStyle
                  cldImg={cloudinary.image(data.receiver_user?.profile_image || DEFAULT_CLOUDINARY_IMAGE)}
                  plugins={[lazyload()]}
                />
                <AppText fontSize={12} fontWeight={600} lineHeight={18}>
                  {data?.receiver_user?.first_name} {data?.receiver_user?.last_name}
                </AppText>
              </FlexDiv>
            ) : (
              <AppText fontSize={12} fontWeight={600} lineHeight={18}>
                Unassigned
              </AppText>
            )}
          </FlexDiv>
        </FlexDiv>

        <FlexDiv align="center" gap={8}>
          <Timestamp data={data} />
          <PhoenixIcon
            svg={expanded ? chevron_up : chevron_down}
            size={16}
            pointer
            onClick={() => setExpanded(!expanded)}
          />
        </FlexDiv>
      </FlexDiv>

      {expanded && <ExpandedLeadAssignmentInfo title={title} initiatedBy={initiatedBy} />}
    </FlexDiv>
  );
};
const ExpandedLeadAssignmentInfo: React.FC<{
  title: string;
  initiatedBy?: string;
}> = ({ title, initiatedBy }) => {
  return (
    <ExpandedContainer>
      <FlexDiv justify="space-between" align="center">
        <FlexDiv gap={16} padding="8px">
          <AppText fontSize={12} fontWeight={600}>
            Origin:
          </AppText>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
            {title}
          </AppText>
        </FlexDiv>
        <FlexDiv gap={16} padding="8px">
          <AppText fontSize={12} fontWeight={600}>
            Initiated by:
          </AppText>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
            {initiatedBy || "N/A"}
          </AppText>
        </FlexDiv>
      </FlexDiv>
    </ExpandedContainer>
  );
};

const SaleInfo: React.FC<{ data: any }> = ({ data }) => {
  const {
    setSelectedSale,
    setShowUpdateProductModal,
    setShowDeleteProductModal,
    setShowSelectProductModal,
    setShowUpdateTermsModal,
  } = useContext(ModalContext);

  const [expanded, setExpanded] = useState(false);

  const [showMiniMenu, setShowMiniMenu] = useState(false);

  const miniMenuItems = useMemo(() => {
    return [
      {
        label: "Edit Product",
        onClick: () => {
          setShowSelectProductModal(true);
          setSelectedSale({
            id: data?.id,
            lead_id: data?.lead_id,
            product_update_modal_type: "edit",
            sale_item_list: data?.sale_items || [],
          });
        },
      },
      {
        label: "Add Product",
        onClick: () => {
          setShowUpdateProductModal(true);
          setSelectedSale({
            id: data?.id,
            lead_id: data?.lead_id,
            product_update_modal_type: "add",
          });
        },
      },
      {
        label: "Delete Product",
        onClick: () => {
          setShowDeleteProductModal(true);
          setSelectedSale({
            product_update_modal_type: "delete",
            sale_item_list: data?.sale_items || [],
          });
        },
      },
      {
        label: "Edit Terms",
        onClick: () => {
          setShowUpdateTermsModal(true);
          setSelectedSale({
            id: data?.id,
            lead_id: data?.lead_id,
            product_update_modal_type: "edit",
          });
        },
      },
    ];
  }, [data]);

  return (
    <FlexDiv direction="column" width="100%">
      <FlexDiv
        justify="space-between"
        gap={4}
        align="center"
        width="100%"
        height={38}
        padding="8px"
        style={{ borderBottom: expanded ? `1px solid ${theme.border.neutral.secondary}` : "unset" }}
      >
        <FlexDiv align="center">
          <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
            Sale
          </HistoryItemTitle>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
            {formatter.format(data?.total_price || 0)}
          </AppText>
        </FlexDiv>

        <FlexDiv align="center" gap={8}>
          {loggedInUser()?.role === "ADMIN" && (
            <PhoenixIcon svg={edit} size={12} pointer onClick={() => setShowMiniMenu(true)} />
          )}
          {showMiniMenu && <MiniMenu items={miniMenuItems} setShowMiniMenu={setShowMiniMenu} />}
          <Timestamp data={data} />
          <PhoenixIcon
            svg={expanded ? chevron_up : chevron_down}
            size={16}
            pointer
            onClick={() => setExpanded(!expanded)}
          />
        </FlexDiv>
      </FlexDiv>
      {expanded && <ExpandedSaleInfo data={data} />}
    </FlexDiv>
  );
};
const ExpandedSaleInfo: React.FC<{
  data: any;
}> = ({ data }) => {
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [data]);
  return (
    <ExpandedContainer>
      <SaleInfoTable>
        <div>
          <FlexDiv
            align="center"
            gap={4}
            padding="4px 8px"
            width="100%"
            data-tip={data?.products}
            data-for="leadhistory-tooltip"
          >
            <CellTitle>Products:</CellTitle>
            <CellValue fontSize={12} noWrap>
              {data?.products}
            </CellValue>
          </FlexDiv>
          <FlexDiv align="center" gap={4} padding="4px 8px" width="100%">
            <CellTitle>Total Price:</CellTitle>
            <CellValue fontSize={12} noWrap>
              {formatter.format(data?.total_price)}
            </CellValue>
          </FlexDiv>
        </div>
        <div>
          <FlexDiv align="center" gap={4} padding="4px 8px" width="100%">
            <CellTitle>Contract Duration:</CellTitle>
            <CellValue fontSize={12} noWrap>
              {data?.contract_duration || "N/A"}
            </CellValue>
          </FlexDiv>
          <FlexDiv align="center" gap={4} padding="4px 8px" width="100%">
            <CellTitle>Payment Terms:</CellTitle>
            <CellValue fontSize={12} noWrap>
              {data?.payment_terms || "N/A"}
            </CellValue>
          </FlexDiv>
        </div>
      </SaleInfoTable>
    </ExpandedContainer>
  );
};

const MiniMenu: React.FC<{
  items: any[];
  setShowMiniMenu: Dispatch<SetStateAction<boolean>>;
}> = ({ items, setShowMiniMenu }) => {
  const ref = useRef(null);
  useClickOutside(ref, () => {
    setShowMiniMenu(false);
  });
  return (
    <MiniMenuContainer ref={ref}>
      {items.map((item, index) => (
        <MiniMenuItem key={`minimenu-${item.value}-${index}`} onClick={() => item?.onClick(item)}>
          <AppText fontSize={12} fontWeight={400}>
            {item.label}
          </AppText>
        </MiniMenuItem>
      ))}
    </MiniMenuContainer>
  );
};

const EmailInfo: React.FC<{ data: any }> = ({ data }) => {
  const { setSelectedEmail, setEmailModal } = useContext(ModalContext);
  const [expanded, setExpanded] = useState(false);
  const emailItem = useMemo(() => data?.email_item, [data]);

  return (
    <FlexDiv direction="column" width="100%">
      <FlexDiv
        justify="space-between"
        gap={4}
        align="center"
        width="100%"
        height={38}
        padding="8px"
        style={{ borderBottom: expanded ? `1px solid ${theme.border.neutral.secondary}` : "unset" }}
      >
        <FlexDiv align="center">
          <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
            {data?.communication_type}
          </HistoryItemTitle>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
            {data?.type === "Sequence Automated Email" ? data?.title : data?.channel}
          </AppText>
        </FlexDiv>

        <FlexDiv align="center" gap={8}>
          {expanded && (
            <PhoenixAppButton
              variant="brand-outline"
              buttonType="ghost-small"
              onClick={() => {
                setSelectedEmail({
                  from: emailItem.from_email,
                  date: moment(emailItem.sent_at).format("MM/DD/YYYY"),
                  time: moment(emailItem.sent_at).format("h:mm A"),
                  subject: emailItem.subject,
                  html: emailItem.html,
                  lead_id: data?.lead?.id,
                  lead_has_opted_out_of_email:
                    data?.lead?.email_sub_status === "OPSIQUnsubscribed" ||
                    data?.lead?.email_sub_status === "LeadUnsubscribed",
                  name: data?.lead?.business_name,
                  email_thread_id: emailItem.email_thread_id,
                  to: emailItem.to_email,
                  channel: emailItem.channel,
                });
                setEmailModal(true);
              }}
            >
              <AppText fontSize={10} fontWeight={600} color={theme.buttontext.brand_outline.default}>
                View Email
              </AppText>
            </PhoenixAppButton>
          )}
          <Timestamp data={data} />
          {!!emailItem && (
            <PhoenixIcon
              svg={expanded ? chevron_up : chevron_down}
              size={16}
              pointer
              onClick={() => setExpanded(!expanded)}
            />
          )}
        </FlexDiv>
      </FlexDiv>
      {expanded && <ExpandedEmailData data={emailItem} />}
    </FlexDiv>
  );
};
const ExpandedEmailData: React.FC<{
  data: any;
}> = ({ data }) => {
  return (
    <ExpandedContainer>
      <FlexDiv direction="column">
        <FlexDiv
          justify="space-between"
          padding="0px 24px 0px 0px"
          style={{ borderBottom: `1px solid ${theme.border.neutral.secondary}` }}
        >
          <FlexDiv align="center" gap={32} height={36} padding="0px 8px">
            <AppText fontSize={12} fontWeight={600}>
              To:
            </AppText>
            <FlexDiv align="center" gap={4}>
              <PhoenixIcon
                svg={mail_incoming}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
              />
              <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                {data?.to_email}
              </AppText>
            </FlexDiv>
          </FlexDiv>
          <FlexDiv align="center" gap={32} height={36} padding="0px 8px">
            <AppText fontSize={12} fontWeight={600}>
              From:
            </AppText>
            <FlexDiv align="center" gap={4}>
              <PhoenixIcon
                svg={mail_outgoing}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
              />
              <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                {data?.from_email}
              </AppText>
            </FlexDiv>
          </FlexDiv>
        </FlexDiv>

        <FlexDiv direction="column" gap={4} padding="4px 8px">
          <AppText fontSize={12} fontWeight={600}>
            Email Subject
          </AppText>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
            {data?.subject}
          </AppText>
        </FlexDiv>
      </FlexDiv>
    </ExpandedContainer>
  );
};

const SMSInfo: React.FC<{ data: any }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const smsItem = useMemo(() => data?.SMSItem, [data]);
  return (
    <FlexDiv direction="column" width="100%">
      <FlexDiv
        justify="space-between"
        gap={4}
        align="center"
        width="100%"
        height={38}
        padding="8px"
        style={{ borderBottom: expanded ? `1px solid ${theme.border.neutral.secondary}` : "unset" }}
      >
        <FlexDiv align="center">
          <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
            {data?.communication_type}
          </HistoryItemTitle>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
            {data?.title}
          </AppText>
        </FlexDiv>

        <FlexDiv align="center" gap={8}>
          <Timestamp data={data} />
          {!!smsItem && (
            <PhoenixIcon
              svg={expanded ? chevron_up : chevron_down}
              size={16}
              pointer
              onClick={() => setExpanded(!expanded)}
            />
          )}
        </FlexDiv>
      </FlexDiv>
      {expanded && <ExpandedSMSData data={smsItem} />}
    </FlexDiv>
  );
};
const ExpandedSMSData: React.FC<{
  data: any;
}> = ({ data }) => {
  return (
    <ExpandedContainer>
      <FlexDiv direction="column">
        <FlexDiv
          justify="space-between"
          padding="0px 24px 0px 0px"
          style={{ borderBottom: `1px solid ${theme.border.neutral.secondary}` }}
        >
          <FlexDiv align="center" gap={32} height={36} padding="0px 8px">
            <AppText fontSize={12} fontWeight={600}>
              To:
            </AppText>
            <FlexDiv align="center" gap={4}>
              <PhoenixIcon
                svg={message_incoming}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
              />
              <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                {data?.recipient_label || "N/A"}
              </AppText>
            </FlexDiv>
          </FlexDiv>
          <FlexDiv align="center" gap={32} height={36} padding="0px 8px">
            <AppText fontSize={12} fontWeight={600}>
              From:
            </AppText>
            <FlexDiv align="center" gap={4}>
              <PhoenixIcon
                svg={message_outgoing}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
              />
              <AppText fontSize={12} fontWeight={400} lineHeight={18}>
                {data?.sender_label || "N/A"}
              </AppText>
            </FlexDiv>
          </FlexDiv>
        </FlexDiv>

        <FlexDiv direction="column" gap={4} padding="4px 8px">
          <AppText fontSize={12} fontWeight={600}>
            Message:
          </AppText>
          <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
            {data?.text}
          </AppText>
        </FlexDiv>
      </FlexDiv>
    </ExpandedContainer>
  );
};

const PhoneInfo: React.FC<{ data: any }> = ({ data }) => {
  const history = useHistory();

  const { setShowEditCallResultModal, setEditCallResultModalData } = useContext(ModalContext);

  const dispositionData = useMemo(() => data?.related_disposition?.computed_call_history_theme, [data]);
  const [expanded, setExpanded] = useState(false);

  return (
    <FlexDiv direction="column" width="100%">
      <FlexDiv justify="space-between" gap={4} align="center" width="100%" padding="8px">
        <FlexDiv align="center" gap={4}>
          <DispositionTag
            fontColor={dispositionData?.completed_with_theme?.font_color || theme.text.neutral.inverse}
            bgColor={dispositionData?.completed_with_theme?.bg_color || theme.fill.brand.primary}
          >
            {dispositionData?.completed_with_theme?.label}
          </DispositionTag>
          <DispositionTag
            fontColor={dispositionData?.call_result_theme?.font_color || theme.text.neutral.inverse}
            bgColor={dispositionData?.call_result_theme?.bg_color || theme.fill.brand.primary}
          >
            {dispositionData?.call_result_theme?.label}
          </DispositionTag>

          <FlexDiv padding="0px 8px 0px 8px" align="center" gap={4}>
            <ContactTag type={data?.lead?.associate_parent_id ? "associate" : "primary"} />
            <AppText fontSize={12} fontWeight={600} lineHeight={18}>
              {!!(data?.lead?.full_name || "").length ? data?.lead?.full_name : "N/A"}
            </AppText>
          </FlexDiv>

          <DurationTag duration={data?.conference?.duration || 0} />
        </FlexDiv>

        <FlexDiv align="center" gap={8}>
          {!!data.conference?.duration && !!data.conference?.id && !!data?.conference?.recording_url_computed && (
            <PhoenixAppButton
              variant="brand-outline"
              buttonType="ghost-small"
              width={20}
              height={20}
              onClick={() => {
                const santizedURL = sanitizeURL(`/call-report/${data.conference.id}/from_system_view=true`);
                history.push(santizedURL);
              }}
            >
              <PhoenixIcon
                svg={play_circle}
                size={12}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
                pointer
              />
            </PhoenixAppButton>
          )}
          {loggedInUser()?.role === "ADMIN" && (
            <PhoenixIcon
              svg={edit}
              size={12}
              pointer
              onClick={() => {
                setShowEditCallResultModal(true);
                setEditCallResultModalData({
                  rep_id: data?.user?.id,
                  lead_activity_id: data?.id,
                  related_disposition: data?.related_disposition,
                });
              }}
            />
          )}
          <Timestamp data={data} />
          {!!data?.conference?.id && (
            <PhoenixIcon
              svg={expanded ? chevron_up : chevron_down}
              size={16}
              pointer
              onClick={() => setExpanded(!expanded)}
            />
          )}
        </FlexDiv>
      </FlexDiv>
      {expanded && <ExpandedPhoneData data={data} />}
    </FlexDiv>
  );
};
const ExpandedPhoneData: React.FC<{
  data: any;
}> = ({ data }) => {
  return (
    <ExpandedContainer>
      <FlexDiv direction="column">
        <FlexDiv
          justify="space-between"
          padding="0px 24px 0px 0px"
          style={{ borderBottom: `1px solid ${theme.border.neutral.secondary}` }}
        >
          <FlexDiv align="center" gap={32} height={36} padding="0px 8px">
            <AppText fontSize={12} fontWeight={600}>
              To:
            </AppText>
            <FlexDiv align="center" gap={4}>
              <PhoenixIcon
                svg={phone_incoming}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
              />
              <AppText fontSize={12} fontWeight={400} lineHeight={18} noWrap>
                {formatPhoneNumber(
                  data?.conference?.channel === "Inbound" ? data?.user?.phone_number : data?.customer_number,
                )}
              </AppText>
            </FlexDiv>
          </FlexDiv>
          <FlexDiv align="center" justify="space-between" padding="0px 8px" width={200}>
            <AppText fontSize={12} fontWeight={600}>
              Key Event:
            </AppText>
            <AppText fontSize={12} fontWeight={400} lineHeight={18}>
              {data?.related_disposition?.associated_action || "N/A"}
            </AppText>
          </FlexDiv>
        </FlexDiv>

        <FlexDiv justify="space-between" padding="0px 24px 0px 0px">
          <FlexDiv align="center" gap={32} height={36} padding="0px 8px">
            <AppText fontSize={12} fontWeight={600}>
              From:
            </AppText>
            <FlexDiv align="center" gap={4}>
              <PhoenixIcon
                svg={phone_outgoing}
                size={16}
                color={theme.icon.brand.default}
                hoverColor={theme.icon.brand.default}
              />
              <AppText fontSize={12} fontWeight={400} lineHeight={18} noWrap>
                {formatPhoneNumber(
                  data?.conference?.channel === "Inbound" ? data?.customer_number : data?.user?.phone_number,
                )}
              </AppText>
            </FlexDiv>
          </FlexDiv>
          <FlexDiv align="center" justify="space-between" padding="0px 8px" width={200}>
            <AppText fontSize={12} fontWeight={600}>
              Duration:
            </AppText>
            <DurationTag duration={data?.conference?.duration || 0} />
          </FlexDiv>
        </FlexDiv>
      </FlexDiv>
    </ExpandedContainer>
  );
};

const NonCallInfo: React.FC<{ data: any }> = ({ data }) => {
  const history = useHistory();
  return (
    <FlexDiv justify="space-between" gap={4} align="center" width="100%" padding="8px">
      <FlexDiv align="center">
        <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
          Non-Call
        </HistoryItemTitle>
        <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
          {data?.type}
        </AppText>
      </FlexDiv>

      <FlexDiv align="center" gap={8}>
        {!!data.conference?.duration && !!data.conference?.id && !!data?.conference?.recording_url_computed && (
          <PhoenixAppButton
            variant="brand-outline"
            buttonType="ghost-small"
            width={20}
            height={20}
            onClick={() => {
              const santizedURL = sanitizeURL(`/call-report/${data.conference.id}/from_system_view=true`);
              history.push(santizedURL);
            }}
          >
            <PhoenixIcon
              svg={play_circle}
              size={12}
              color={theme.icon.brand.default}
              hoverColor={theme.icon.brand.default}
              pointer
            />
          </PhoenixAppButton>
        )}
        <Timestamp data={data} />
      </FlexDiv>
    </FlexDiv>
  );
};

const ExternalTransferInfo: React.FC<{ data: any }> = ({ data }) => {
  return (
    <FlexDiv justify="space-between" gap={4} align="center" width="100%" padding="8px">
      <FlexDiv align="center">
        <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
          {data?.type}
        </HistoryItemTitle>
        <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
          {`${data?.user?.first_name} ${data?.user?.last_name} to `}
          {formatPhoneNumber(data?.external_transfer_number)}
        </AppText>
      </FlexDiv>

      <Timestamp data={data} />
    </FlexDiv>
  );
};

const SequenceEntryExitInfo: React.FC<{ data: any }> = ({ data }) => {
  return (
    <FlexDiv justify="space-between" gap={4} align="center" width="100%" padding="8px">
      <FlexDiv align="center">
        <HistoryItemTitle fontSize={12} fontWeight={600} lineHeight={18}>
          Sequence {data?.exit_type ? "Exit" : "Entry"}
        </HistoryItemTitle>
        <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.tertiary}>
          {data?.sequence?.name}
        </AppText>
      </FlexDiv>

      <Timestamp data={data} />
    </FlexDiv>
  );
};

const IndicatorContainer = styled.div<{ borderColor: string | null; color: string }>`
  position: relative;
  z-index: 1;
  top: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  margin-top: -12px;

  background-color: ${({ color }) => color};
  border: 1px solid ${({ color, borderColor }) => borderColor || color};
  border-radius: 4px;
`;

const InfoContainer = styled.div<{ hasUnderInfo: boolean }>`
  display: flex;
  align-items: center;

  width: 100%;
  min-height: 40px;

  border: 1px solid ${theme.border.neutral.secondary};
  border-radius: 8px;
  background-color: ${theme.fill.neutral.primary};

  margin-bottom: ${({ hasUnderInfo }) => (hasUnderInfo ? "8px" : "12px")};
`;

const UnderDataContainer = styled.div`
  padding: 0px 8px;
  margin-bottom: 12px;
`;

const AdvancedImageStyle = styled(AdvancedImage)`
  border-radius: 24px;
  width: 24px;
  height: 24px;
`;

const HistoryItemTitle = styled(AppText)`
  margin-right: 8px;
`;

const Ruler = styled.div`
  position: absolute;
  left: 12px;

  width: 1px;
  height: 100%;

  background-color: ${theme.border.neutral.secondary};
`;

const HistoryItemContainer = styled.div`
  position: relative;

  display: flex;
  gap: 8px;

  width: 100%;
  height: fit-content;

  &:first-of-type ${Ruler} {
    top: 20px;
    height: calc(100% - 20px);
  }
  &:last-of-type ${Ruler} {
    bottom: 20px;
  }

  animation: ${theme.lightPopup} 0.2s ease-in-out forwards;
`;

const ExpandedContainer = styled.div`
  width: 100%;
  padding: 0px 8px;
`;
const SaleInfoTable = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  width: 100%;

  & > div:nth-child(1) {
    display: grid;
    grid-template-rows: repeat(2, 36px);
    width: 100%;

    & > div {
      border-bottom: 1px solid ${theme.border.neutral.secondary};
      &:last-child {
        border-bottom: none;
      }
    }
  }

  & > div:nth-child(2) {
    display: grid;
    grid-template-rows: repeat(2, 36px);
    width: 100%;

    & > div {
      border-bottom: 1px solid ${theme.border.neutral.secondary};
      &:last-child {
        border-bottom: none;
      }
    }
  }
`;

const MiniMenuContainer = styled.div`
  position: absolute;
  z-index: 1;

  width: fit-content;
  min-width: 124px;

  height: fit-content;
  top: 32px;

  border: 1px solid ${theme.border.neutral.primary};
  border-radius: 4px;

  overflow: hidden;

  animation: ${theme.lightPopup} 0.2s ease-in-out forwards;
`;
const MiniMenuItem = styled.div`
  display: flex;
  align-items: center;

  padding: 8px;

  background-color: ${theme.fill.neutral.primary};
  :hover {
    background-color: ${theme.fill.brand.tertiary};
    & > p {
      color: ${theme.text.brand.primary};
      transition: color 0.15s ease-in-out;
    }
  }
  transition: background-color 0.15s ease-in-out;
  cursor: pointer;
`;

const CellTitle = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  gap: 4px;

  width: 100px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
`;
const CellValue = styled(AppText)`
  max-width: 60%;
  @media (max-width: 1870px) {
    max-width: 50%;
  }
  @media (max-width: 1792px) {
    max-width: 35%;
  }
  @media (max-width: 1690px) {
    max-width: 25%;
  }
  @media (max-width: 1568px) {
    max-width: 32%;
  }

  text-overflow: ellipsis;
  overflow: hidden;
`;

const DispositionTag = styled.div<{ fontColor: string; bgColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: fit-content;
  height: 24px;
  padding: 4px 8px;

  border-radius: 4px;
  background-color: ${({ bgColor }) => bgColor};

  color: ${({ fontColor }) => fontColor};
  font-size: 8px;
  font-weight: 600;
  line-height: 12px;
  text-transform: uppercase;
`;

const DurationTag: React.FC<{ duration: number }> = ({ duration }) => {
  // 00:00 or 00:00:00 depending on length
  const formattedDuration = useMemo(() => {
    if (!duration || duration <= 0) return "00:00";

    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    }

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }, [duration]);

  return (
    <DurationTagContainer>
      <AppText fontSize={10} fontWeight={400} lineHeight={14}>
        {formattedDuration}
      </AppText>
    </DurationTagContainer>
  );
};

const DurationTagContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 4px 8px;
  border: 1px solid ${theme.border.neutral.primary};
  border-radius: 4px;
`;
